/** «IMPORTĂ PREȚURILE PIEȚEI» în proiect: o previzualizare înainte de orice
 *  scriere — fiecare țintă cu prețul de acum, prețul pieței (cel mai mic valid,
 *  pe baza catalogului), diferența și sursa — iar omul debifează ce nu vrea.
 *
 *  Nimic nu se schimbă singur: o colectare nouă nu rescrie proiectele, iar un
 *  preț pus de om (sau de o ofertă) vine nebifat, ca un import să nu-l șteargă
 *  din reflex. Un rând fără cursul proiectului nu se poate importa: prețul
 *  pieței e în lei, iar proiectul socotește în EUR. */
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api";
import SeIncarca from "../../components/SeIncarca";
import { data, esteCheie, numar, t, tn, useT } from "../../i18n";
import { pretUnitar } from "../../structure/bani";
import { grupatePeFaze, numeFaza } from "./boq";
import { Sertar } from "./ExploreazaPiata";
import { Lamurire } from "./Termeni";
import {
  aplicaDinPropunere, bifateImplicit, getPiataResurse, importabila, importaPiata, umPiata,
  type PiataProiect, type Propunere, type RaspunsImport,
} from "./piataResurse";
import "./piataResurse.css";

const textMotiv = (m: string) => {
  const k = `piata_res.motiv.${m}`;
  return esteCheie(k) ? t(k) : m;
};

const diferenta = (p: Propunere) =>
  `${p.diferenta_pct > 0 ? "+" : ""}${numar(p.diferenta_pct, { maximumFractionDigits: 1 })} %`;

export default function ImportPiata({ projectId, solutieId, potScrie, motivBlocat = null, laInchide, laSchimbat }: {
  projectId: string; solutieId: string; potScrie: boolean;
  /** de ce nu se poate scrie acum, deși omul are drept (schimbări nesalvate pe pagină) */
  motivBlocat?: string | null;
  laInchide: () => void;
  /** după import: părintele reface catalogul și lista */
  laSchimbat: () => void;
}) {
  useT();
  const [date, setDate] = useState<PiataProiect | null>(null);
  const [bifate, setBifate] = useState<Set<string>>(new Set());
  const [rezultat, setRezultat] = useState<RaspunsImport | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const incarca = useCallback(async () => {
    try {
      const d = await getPiataResurse(projectId, solutieId);
      setDate(d);
      setBifate(bifateImplicit(d.propunere));
      setEroare(null);
    } catch (e) {
      setEroare(t("piata_res.nu_s_a_citit", { e: (e as Error).message }));
    }
  }, [projectId, solutieId]);
  useEffect(() => { void incarca(); }, [incarca]);

  const titlu = t("piata_res.import.titlu");
  if (!date) {
    return (
      <Sertar titlu={titlu} laInchide={laInchide}>
        {eroare ? <div className="sg-error">{eroare}</div> : <SeIncarca />}
      </Sertar>
    );
  }

  const propunere = date.propunere;
  const alese = aplicaDinPropunere(propunere, bifate);
  const comuta = (id: string) => setBifate((b) => {
    const n = new Set(b);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  async function importa() {
    setBusy(true);
    setEroare(null);
    try {
      setRezultat(await importaPiata(projectId, solutieId, alese));
      laSchimbat();
    } catch (e) {
      setEroare(e instanceof ApiError && e.status === 409 ? t("lc.catalog.calcat") : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (rezultat) {
    const nume = (id: string) => propunere.find((p) => p.tinta === id)?.nume ?? id;
    return (
      <Sertar titlu={titlu} laInchide={laInchide}>
        <div className="pr-cap">
          <div className="pr-mesaj" role="status">{tn("piata_res.pus#n", rezultat.aplicate.length)}</div>
          {rezultat.respinse.length > 0 && (
            <div className="sg-muted">
              {t("piata_res.respinse", { l: rezultat.respinse.map((x) => `${nume(x.tinta)} (${x.motiv})`).join("; ") })}
            </div>
          )}
          <div><button type="button" className="btn btnp" onClick={laInchide}>{t("piata_res.inchide")}</button></div>
        </div>
      </Sertar>
    );
  }

  return (
    <Sertar titlu={titlu} laInchide={laInchide}>
      <div className="pr-cap">
        {propunere.length > 0 && (
          <div className="sg-muted">
            {tn("piata_res.import.au_pret#n", propunere.length)}
            {date.colectat_la && <> · {t("piata_res.colectat", { la: data(date.colectat_la) })}</>}
            <Lamurire text={t("piata_res.pe_proiect")} />
          </div>
        )}
        {date.curs == null && propunere.length > 0 && <div className="sg-muted">{t("piata_res.fara_curs")}</div>}
        {motivBlocat && potScrie && <div className="sg-muted">{motivBlocat}</div>}
        {eroare && <div className="sg-error">{eroare}</div>}
      </div>
      {propunere.length === 0 ? (
        <div className="sg-muted">{t("piata_res.import.gol")}</div>
      ) : (
        <div className="bc-scroll">
          <table className="sg-table lc-tabel pr-tabel">
            <thead>
              <tr>
                <th />
                <th>{t("piata_res.import.th.resursa")}</th>
                <th className="bc-n">{t("piata_res.import.th.acum")}</th>
                <th className="bc-n">{t("piata_res.import.th.piata")}</th>
                <th className="bc-n">{t("piata_res.import.th.dif")}</th>
                <th>{t("piata_res.th.sursa")}</th>
              </tr>
            </thead>
            <tbody>
              {grupatePeFaze(propunere, (p) => p.etapa ?? null, date.etape ?? []).map((g) => [
                <tr key={`faza:${g.etapa ?? "-"}`} className="lc-faza">
                  <td colSpan={6}>{numeFaza(date.etape ?? [], g.etapa)}</td>
                </tr>,
                ...g.randuri.map((p) => {
                const um = umPiata(p.um);
                const ok = importabila(p);
                return (
                  <tr key={p.tinta} className={ok ? undefined : "pr-echivalent"}>
                    <td>
                      <input type="checkbox" aria-label={p.nume} checked={bifate.has(p.tinta)}
                             disabled={!ok || !potScrie} onChange={() => comuta(p.tinta)} />
                    </td>
                    <td>
                      {p.nume}
                      {p.motiv && <div className="sg-muted">{textMotiv(p.motiv)}</div>}
                    </td>
                    <td className="sg-mono bc-n">{pretUnitar(p.pret_eur, um)}</td>
                    <td className="sg-mono bc-n">{pretUnitar(p.piata_eur, um)}</td>
                    <td className={`sg-mono bc-n ${p.diferenta_pct > 0 ? "pr-dif-sus" : p.diferenta_pct < 0 ? "pr-dif-jos" : ""}`}>
                      {diferenta(p)}
                    </td>
                    <td>
                      {p.sursa_nume}
                      <div className="sg-muted">{t("piata_res.vazut_la", { la: data(p.vazut_la) })}</div>
                    </td>
                  </tr>
                );
                }),
              ])}
            </tbody>
          </table>
        </div>
      )}
      <div className="pr-actiuni pr-jos">
        <span className="cp-sp" />
        <button type="button" className="btn" onClick={laInchide}>{t("comun.renunta")}</button>
        <button type="button" className="btn btnp" disabled={!potScrie || !!motivBlocat || busy || alese.length === 0}
                onClick={() => void importa()}>
          {busy ? t("piata_res.lucrez") : tn("piata_res.import.importa#n", alese.length)}
        </button>
      </div>
    </Sertar>
  );
}
